import React, {useCallback, useEffect, useState} from "react";
import {
    Grid,
    Typography,
    Avatar,
    Box,
    Container,
    GridItemsAlignment,
    CircularProgress,
} from "@material-ui/core";
import {useHistory, useLocation} from "react-router-dom";
import {makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {useSnackbar} from "notistack";
import moment from "moment";

import {Navigation} from "../../Components";
import Calendar from "../../Components/Calendar";
import AvaliableVetListItem from "../../Components/ListItems/AvaliableVet";
import {specialities} from "../../Constants/specialities";
import {colorWhite} from "../../Constants/theme";
import {BirdIconSVG, ChickenIconSVG, PetCatSVG, PetDogSVG} from "../../utils/petTypeIcons";
import {ReactComponent as LogoSVG} from "../../Images/logo.svg";
import {ReactComponent as AnimalSVG} from "../../Images/addPetIcon.svg";
import Image from "material-ui-image";
import API from "../../helpers/apiHelpers";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {getCreditRequestsCount, getCreditsExpirationDate} from "../../redux/credits/credits.actions";
import {getAddress, getUserProfile} from "../../redux/userProfile/userProfile.actions";
import {setPrice} from "../../redux/scheduleAppointment/scheduling.reducers";
import {
    cleanUpPhotos,
    cleanUpVideo,
    updateIsImmediate,
    updatePaymentMethod,
    updatePickedDate,
    updatePickedTimeSlot,
    updateRequestReason,
    updateSelectedPetID,
    updateSelectedState,
    updateServiceType,
    updateVetId,
    updateVetProfile,
    updateVetProfileId,
} from "../../redux/scheduleAppointment/scheduling.actions";
import {setPetType} from "../../utils/petInfo";
import withLocation from "../../hocs/withLocation";
import {setClientPrices, setSelectedPrice} from "../../redux/common/common.reducers";

const useStyle = makeStyles((theme: Theme) => ({
    wrapper: {
        height: "100%",
        marginTop: 90,
        marginBottom: 75
    },
    rowContainerFirst: {
        padding: 10,
    },
    rowContainer: {
        marginTop: "20px",
        padding: 10,
    },
    avatar: {
        width: 95,
        height: 95,
        cursor: "pointer",
        backgroundColor: colorWhite
    },
    selectedPet: {
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
        borderRadius: 5,
    },
    sectionText: {
        textAlign: "left",
    },
    section: {
        width: "100%",
        marginBottom: 32,
    },
    bookingContent: {
        width: "100%",
        maxWidth: "100%",
    },
    slotsSection: {
        width: "100%",
    },
    sectionTitle: {
        marginBottom: 16,
        textAlign: "left",
    },
    leftAligned: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
    },
}));

const SERVICE_TYPE = "RX_VIDEO" as const;
const REASON_TYPE = "PRESCRIPTION" as const;

const getTimeslotsForSelectedDate = (scheduling, dateTime) => {
    const dateTimeSlots = scheduling.find((item) => moment(item.date).isSame(dateTime, "day"));
    return (dateTimeSlots || {timeslots: []}).timeslots;
};

const getSchedulingForUserTimezone = (schedule) => {
    const newSchedule = schedule
        .reduce((acc, item) => [...item.timeslots, ...acc], [])
        .sort((a, b) => moment(a).diff(moment(b)))
        .map((item) => moment.tz(item, moment.tz.guess()))
        .filter((timeslot) => timeslot > moment())
        .reduce((acc, item) => {
            const key = item.format("YYYY-MM-DD");
            if (!acc[key]) {
                acc[key] = {date: key, timeslots: []};
            }
            acc[key].timeslots.push(item.format());
            return acc;
        }, {});
    return Object.values(newSchedule);
};

interface ISheduleProps {
    getLocation: () => void
}

const Schedule: React.FC<ISheduleProps> = ({getLocation}) => {
    const classes = useStyle();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const theme = useTheme();
    const path = useLocation();
    const {enqueueSnackbar} = useSnackbar();
    const timeZone = moment.tz.guess();

    const [petsList, setPetsList] = useState([]);
    const [selectedPet, setSelectedPet] = useState(null);
    const selectedPetName = (() => {
    const pet = petsList.find(pet => pet.id === selectedPet);
        return pet?.name
            ? pet.name
                .trim()
                .toLowerCase()
                .replace(/\b\w/g, character => character.toUpperCase())
            : "your pet";
    })();
    const [vetProfiles, setVetProfiles] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [prices, setPrices] = useState([]);

    const partnerId = useSelector((state: RootState) => state?.branding?.partnerId);
    const location = useSelector((state: RootState) => state?.common.location);
    const user = useSelector((state: RootState) => state?.user);
    const userProfile = useSelector((state: RootState) => state?.userProfile);
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const clientPrices = useSelector((state: RootState) => state?.common?.prices);
    const branding = useSelector((state: RootState) => state?.branding);
    const profileState = userProfile?.address?.state || userProfile?.stateOfResidence || null;

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const alignResonsive: GridItemsAlignment = isMobile ? "flex-start" : "center";
    const justifyResonsive: GridItemsAlignment = "center";

    useEffect(() => {
        if (user?.userId && !userProfile?.address?.state) {
            dispatch(getAddress(user.userId));
        }
    }, [dispatch, user?.userId, userProfile?.address?.state]);

    useEffect(() => {
        (async () => {
            if (user.role === "VET") {
                history.push("/");
            }
            if (path.state) {
                history.push(`joinRoom${path.state}`);
            }
            if (user.userId) {
                try {
                    const profile = await dispatch(getUserProfile({userId: user.userId}));
                    const response = await API.getAnimal(user.userId);
                    setPetsList(response);
                    if (!profile.length) {
                        enqueueSnackbar("You have no profile yet, you will be redirected to create it in a moment");
                        setTimeout(() => {
                            history.push({pathname: "/dashboardProfile"});
                        }, 3000);
                    } else if (response.length) {
                        setSelectedPet(response.length === 1 ? response[0].id : null);
                    } else {
                        enqueueSnackbar("There are no pets in your pets list, you will be redirected to edit page of your first pet in a moment");
                        setTimeout(() => {
                            history.push({pathname: "/pets/0/general"});
                        }, 3000);
                    }
                    if (!isWhiteLabel) {
                        dispatch(getCreditsExpirationDate());
                        dispatch(getCreditRequestsCount());
                    }
                    await getLocation();
                } catch (err) {
                    enqueueSnackbar(err.errorMessage, {
                        autoHideDuration: 3000,
                        variant: "error"
                    });
                }
            } else {
                document.location.reload();
            }
        })();
    }, []);

    useEffect(() => {
        setPrices([{
            type: SERVICE_TYPE,
            label: `Video - $${clientPrices.rxVideo}`,
            checked: false
        }]);
    }, [clientPrices]);

    useEffect(() => {
        (async () => {
            if (location !== undefined) {
                const price = await API.getApptPrices(partnerId, location);
                dispatch(setClientPrices(price));
            }
        })();
    }, [location, partnerId, dispatch]);

    const getSelectedPrice = () => prices.find(el => el.type === SERVICE_TYPE)?.label.split(" ")[2]?.split("$")[1]
        || clientPrices.rxVideo
        || "55";

    const getWorkingStates = () => (profileState ? [profileState] : null);

    const buildVetSearchParams = useCallback((day: Date) => {
        const date = moment(day).tz(timeZone, true);
        return {
            type: "VET",
            workingStates: getWorkingStates(),
            prescriptionDesire: true,
            specialties: [],
            clientTimeZone: timeZone,
            rangeBegins: date.format("YYYY-MM-DD"),
            rangeEnds: date.clone().add(1, "days").format("YYYY-MM-DD"),
        };
    }, [timeZone, profileState]);

    const appendPetSpecialty = async (param: Record<string, unknown>, petId: number) => {
        const pet = await API.getAnimalById(petId);
        const petType = pet.type === "Other" ? pet.breed : pet.type;
        const filteredType = specialities.filter(speciality => speciality?.value === petType);
        param.specialties = filteredType.length > 0 ? [filteredType[0]?.value] : [petType || ""];
        return param;
    };

    const fetchVetsForDate = useCallback(async (day: Date, petId: number) => {
        if (!petId) {
            setVetProfiles([]);
            return;
        }
        setShowSpinner(true);
        setSelectedDate(day);
        try {
            const param = await appendPetSpecialty(buildVetSearchParams(day), petId);
            const profiles = await API.getProfiles(param);
            setVetProfiles(profiles || []);
        } catch (err) {
            console.error(err);
            setVetProfiles([]);
        } finally {
            setShowSpinner(false);
        }
    }, [buildVetSearchParams]);

    useEffect(() => {
        if (selectedPet) {
            fetchVetsForDate(selectedDate, selectedPet);
        } else {
            setVetProfiles([]);
            setAvailableTimeSlots([]);
        }
    }, [selectedPet, profileState, fetchVetsForDate]);

    useEffect(() => {
        if (!vetProfiles.length) {
            setAvailableTimeSlots([]);
            return;
        }
        const newTimeSlots = [];
        vetProfiles.forEach((profile) => {
            const schedulingForUserTimezone = getSchedulingForUserTimezone(profile.schedulingDays);
            const timeslots = getTimeslotsForSelectedDate(schedulingForUserTimezone, selectedDate);
            newTimeSlots.push(...timeslots);
        });
        setAvailableTimeSlots(newTimeSlots);
    }, [vetProfiles, selectedDate]);

    const handleCalendarChange = (day: Date) => {
        if (!selectedPet) {
            enqueueSnackbar("Please select a pet!", {variant: "error"});
            return;
        }
        fetchVetsForDate(day, selectedPet);
    };

    const handleTimeSlotSelect = (
        profile: object,
        profileId: number,
        userId: number,
        timeslot: string
    ) => {
        if (!selectedPet) {
            enqueueSnackbar("Please select a pet!", {variant: "error"});
            return;
        }
        dispatch(updateSelectedPetID(selectedPet));
        dispatch(updateSelectedState(profileState || location));
        dispatch(updateRequestReason(REASON_TYPE));
        dispatch(updateServiceType(SERVICE_TYPE));
        dispatch(updateIsImmediate(false));
        dispatch(updatePaymentMethod("WITHOUT_CREDITS"));
        dispatch(setSelectedPrice(getSelectedPrice()));
        dispatch(setPrice(getSelectedPrice()));
        dispatch(updateVetProfileId(profileId));
        dispatch(updateVetProfile(profile));
        dispatch(updateVetId(userId));
        dispatch(updatePickedDate(moment(timeslot).toISOString()));
        dispatch(updatePickedTimeSlot([moment(timeslot).format()]));
        dispatch(cleanUpPhotos());
        dispatch(cleanUpVideo());
        history.push("/summary");
    };

    return (
        <Container maxWidth="md">
            <Box marginTop={isMobile ? "30px" : "100px"} marginBottom="75px" paddingBottom="20px">
                <Grid container spacing={isMobile ? 0 : 3}>
                    {!isMobile &&
                    <Grid item xs={3}>
                        <Navigation/>
                    </Grid>
                    }

                    <Grid item md={9} xs={12} className={classes.bookingContent}>
                        <Grid container direction="column">
                            <Grid item xs={12}>
                                <Box marginBottom="59px" width="100%">
                                        <Grid container className={classes.rowContainerFirst} alignItems={alignResonsive}
                                            justify={justifyResonsive}>
                                            <Typography variant="h3" className={classes.sectionText}>
                                                Who needs an appointment
                                            </Typography>
                                        </Grid>
                                        <Grid container className={classes.rowContainer} justify="center"
                                            alignItems={alignResonsive}>
                                            {petsList.map((pet) => (
                                                <Grid xs={6} sm={4} item container justify="center" key={pet.id}>
                                                    <Box onClick={() => { setSelectedPet(pet.id) }} >
                                                        <Avatar variant="square"
                                                            className={`${classes.avatar} ${selectedPet === pet.id ? classes.selectedPet : ""}`}>
                                                            {!isWhiteLabel ?
                                                                ({
                                                                    "FELINE": <Image alt="cat image" src={pet.imageUrl}
                                                                        errorIcon={<PetCatSVG/>}
                                                                        imageStyle={{objectFit: "cover"}}
                                                                        style={{width: "100%"}}/>,
                                                                    "CANINE": <Image alt="dog image" src={pet.imageUrl}
                                                                        errorIcon={<PetDogSVG/>}
                                                                        imageStyle={{objectFit: "cover"}}
                                                                        style={{width: "100%"}}/>,
                                                                    "AVIAN": <Image alt="bird image" src={pet.imageUrl}
                                                                        errorIcon={<BirdIconSVG/>}
                                                                        imageStyle={{objectFit: "cover"}}
                                                                        style={{width: "100%"}}/>,
                                                                    "CHICKEN": <Image alt="chicken image" src={pet.imageUrl}
                                                                        errorIcon={<ChickenIconSVG/>}
                                                                        imageStyle={{objectFit: "cover"}}
                                                                        style={{width: "100%"}}/>,
                                                                }[setPetType(pet.type)] ||
                                                                    <Box display="flex" justifyContent="center"
                                                                        alignItems="center" width="100px" height="100px">
                                                                        {pet.imageUrl &&
                                                                        <Image alt="pet image"
                                                                            imageStyle={{objectFit: "cover"}}
                                                                            src={pet.imageUrl}
                                                                            errorIcon={<AnimalSVG/>}
                                                                            style={{width: "100%",  backgroundColor: '#EFDEC6'}}/>}
                                                                    </Box>
                                                                )
                                                                :
                                                                <Box display="flex" justifyContent="center"
                                                                    alignItems="center" width="100px" height="100px">
                                                                    <Image alt="pet image"
                                                                        imageStyle={{objectFit: "cover"}}
                                                                        src={pet.imageUrl}
                                                                        errorIcon={<img alt="error icon" src={branding.logo} width="100%"/>}
                                                                        style={{width: "100%"}}/>
                                                                </Box>
                                                            }
                                                        </Avatar>
                                                        <Typography style={{
                                                            overflowX: "hidden",
                                                            textOverflow: "ellipsis",
                                                            maxWidth: "95px",
                                                            whiteSpace: "nowrap"
                                                        }}>
                                                            {pet.name}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        <Box className={classes.section} marginTop={3}>
                                            <Grid container className={classes.rowContainer} alignItems={alignResonsive}
                                                  justify={justifyResonsive}>
                                                <Typography variant="h3" className={classes.sectionTitle}>
                                                    When would you like your virtual visit?
                                                </Typography>
                                            </Grid>
                                            <Calendar
                                                value={selectedDate || new Date()}
                                                handleChange={handleCalendarChange}
                                                alignLeft
                                                fullWidth
                                                loading={showSpinner}
                                            />
                                        </Box>

                                        <Box className={`${classes.section} ${classes.slotsSection}`}>
                                            <Grid container className={classes.rowContainer} alignItems={alignResonsive}
                                                  justify={justifyResonsive}>
                                                <Typography variant="h3" className={classes.sectionTitle}>
                                                    Choose a time that works best for you
                                                </Typography>
                                            </Grid>
                                            {selectedPet && (
                                                <Typography variant="body2" color="textSecondary" style={{marginBottom: 16}}>
                                                    {moment(selectedDate).format("dddd, MMMM D, YYYY")}
                                                </Typography>
                                            )}
                                            {!selectedPet && (
                                                <Typography color="textSecondary">
                                                    Select your pet to view available appointment times.
                                                </Typography>
                                            )}
                                            {showSpinner && (
                                                <Box className={classes.leftAligned}>
                                                    <CircularProgress />
                                                </Box>
                                            )}
                                            {selectedPet && (!vetProfiles.length || !availableTimeSlots.length) && !showSpinner && (
                                                <Box className={classes.leftAligned}>
                                                    <Typography>
                                                        We don&apos;t have any openings on {moment(selectedDate).format("dddd, MMMM D, YYYY")} right now. Take a look at our other available dates and find a time that works for you and {selectedPetName}.
                                                    </Typography>
                                                </Box>
                                            )}
                                            {selectedPet && vetProfiles.length > 0 && availableTimeSlots.length > 0 && !showSpinner &&
                                                vetProfiles.map((profile) => {
                                                    const schedulingForUserTimezone = getSchedulingForUserTimezone(profile.schedulingDays);
                                                    const timeslots = getTimeslotsForSelectedDate(schedulingForUserTimezone, selectedDate);
                                                    return (
                                                        <AvaliableVetListItem
                                                            name={`Dr. ${profile.lastName || ""}`}
                                                            key={profile.id}
                                                            profileId={profile.id}
                                                            image={profile.image}
                                                            userId={profile.userId}
                                                            profile={profile}
                                                            speciality={profile.speciality}
                                                            scheduling={timeslots}
                                                            onTimeSlotSelect={(timeslot) =>
                                                                handleTimeSlotSelect(profile, profile.id, profile.userId, timeslot)
                                                            }
                                                        />
                                                    );
                                                })}
                                        </Box>

                                        {isWhiteLabel &&
                                        <Box display="flex" justifyContent="center" alignItems="center" marginTop="15px">
                                            <Typography color="primary" style={{fontSize: 14, marginRight: "10px"}}>
                                                Powered by
                                            </Typography>
                                            <LogoSVG style={{width: 47}}/>
                                        </Box>
                                        }
                                    </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default withLocation(Schedule);
